import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { ErrorMessageService } from '@prisma/services/error-message.service';
import { _error, _isFeDev, _log, _warn } from '@shared/aux_helper_environment';
import { _get } from '@shared/aux_helper_functions';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { DevSettingsService, FeatureFlagsKeysEnum } from 'core/services/dev-settings.service';
import { GlobalAlertService, IanTranslateService } from 'core/services/ian-core-singleton.service';
import { environment } from 'environments/environment';
import { NgxPermissionsService } from 'ngx-permissions';
import { Observable } from 'rxjs';
import { catchError, delay, map, retryWhen, take, tap } from 'rxjs/operators';
import { HttpCancelService } from './http-cancel.service';
import { MessagingService } from './messaging.service';
import { StatusConecctionService } from './status_conecction.service';
import { TempAccessSessionService } from './temp-access-session.service';

const _devTest = false;
const _devTestErrors = false && _isFeDev();
const _devTestTimeOut = false && _isFeDev();
const _devTestErrorsTO: number = 1024 * 5;
const _keyVer = environment.localStorageKeyVerPrefix || '_v3_';

const _saveToken = true;
let _tokenIsSaved = false;
// Toggle para activar/desactivar el flujo de acceso temporal de invitado en este interceptor.
const _enableTempGuestFlow = true;

/*
  PLATFORM-SEC02 (CA-06): el API Gateway responde 401 con {"error":"user_blocked"} cuando un
  administrador bloqueó al usuario y su sesión ya estaba abierta.

  Hay que distinguirlo del 401 común (token vencido). Ante un 401 común el interceptor pide
  autorización de nuevo, que es lo correcto; con un usuario bloqueado eso entra en bucle: la app
  re-autoriza, IdentityServer emite un código y el gateway vuelve a cortar.

  El cuerpo puede llegar como objeto ya parseado o como string, según el responseType del request.
*/
const _isBlockedUserError = (err: any): boolean => {
  const body = err?.error;

  if (!body) return false;

  if (typeof body === 'string') return body.includes('user_blocked');

  return _get(body, 'error') === 'user_blocked';
};

const _getFormatDate = (date?: any, outputArray = false): Date | any => {
  let dateString: string = null;

  if (date == null) {
    dateString = new Date().toISOString();
  } else if (typeof date === 'string') {
    dateString = date;
  } else if (date?.toISOString != null) {
    dateString = (date as Date).toISOString();
  } else if (typeof date === 'number') {
    dateString = new Date(date).toISOString();
  }

  if (!(typeof dateString === 'string') || !dateString.includes('T') || !(dateString?.length > 0)) {
    _error('Fecha no valida, se toma el día de hoy, fecha: ' + date + ' / ' + dateString);
    return new Date();
  }

  dateString = new Date(Date.now()).toISOString();
  const dateStringArr = dateString.split('T')[0].replace(/-/g, '/').split('/');

  if (outputArray) return dateStringArr;

  if (dateStringArr.length < 3) {
    _error('Fecha no valida, se toma el día de hoy, fecha: ' + date + ' / ' + dateString);
    return new Date();
  }

  return new Date(Number(dateStringArr[0]), Number(dateStringArr[1]) - 1, Number(dateStringArr[2]));
};

@Injectable()
export class TokenInterceptorService implements HttpInterceptor {
  [x: string]: any;
  private oidcSecurityService: OidcSecurityService;

  constructor(
    private http: HttpClient,
    private injector: Injector,
    private router: Router,
    private httpCancelService: HttpCancelService,
    private permissionsService: NgxPermissionsService,
    private errorMessageService: ErrorMessageService,
    private messagingService: MessagingService,
    private statusConecctionService: StatusConecctionService,
    private translate: IanTranslateService,
    private globalAlertService: GlobalAlertService,
    private devSettingsService: DevSettingsService,
    private tempAccessSession: TempAccessSessionService
  ) {
    if (_devTestErrors) this.testErrorsEP();
    if (_devTestTimeOut) this.testTimeOutEP();

    // Exponer método de chaos testing en window para pruebas desde consola
    (window as any).__testChaos = this.testChaosMode.bind(this);
  }

  testErrorsEP() {
    setTimeout(() => {
      this.__testEP_errorCode();
    }, _devTestErrorsTO);
  }

  testTimeOutEP() {
    setTimeout(() => {
      this.__testEP_timeOut();
    }, _devTestErrorsTO);
  }

  private __testEP_errorCode() {
    let urlsErrorToTest = ['assets/test.error_warning.json', 'assets/test.error_no_ok.json', 'assets/test.error.json'];

    this.http
      .get(urlsErrorToTest[0])
      .pipe(
        tap(data => (false ? _log('\n\n[__testEP_error] ok', data) : null)),
        catchError(error => {
          if (false) _log('\n\n[__testEP_error] fail', error);
          return null;
        })
      )
      .subscribe();
  }

  private __testEP_timeOut() {
    const cudOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json', timeout: `${120000}` }),
    };

    let payLoad =
      '{"states":[1,2],"actionTypes":[],"priceTypes":[],"itemType":null,"onlyParents":true,"categories":[],"subcategories":[],"query":"","storeTags":[],"itemTags":[],"ruleTypes":[],"groupId":null}';

    let URL = _get(environment, 'apiBaseUrl_prices') + 'decisions/kpis';

    console.time();

    this.http
      .post(URL, payLoad, cudOptions)
      .pipe(
        tap(data => {
          _log('\n\n\n__testEP_timeOut', data);
          console.timeEnd();
        }),
        catchError(error => {
          _log('\n\n\n__testEP_timeOut', error);
          console.timeEnd();
          return null;
        })
      )
      .subscribe();
  }

  private checkCustomError($event) {
    const eventError = $event;

    const responseSuccess = eventError?.body?.responseSuccess !== false;
    const requestUrl = String(eventError?.url || '').toLowerCase();
    /** Pre-aprobación / rechazo (detalle y masivo) y publicación: si `responseSuccess` es true, no mostrar avisos del body. */
    const isAssortmentSuggestionDecisionRequest =
      requestUrl.includes('/rangeengine/suggestions/pre-approve') ||
      requestUrl.includes('/rangeengine/suggestions/massive-pre-approve') ||
      requestUrl.includes('/rangeengine/suggestions/publish') ||
      requestUrl.includes('/rangeengine/suggestions/massive-publish');

    if (false) console.warn('[checkCustomError]', eventError);

    let responseMessages = eventError?.body?.responseMessages;
    if (responseMessages && responseMessages.length) {
      responseMessages = responseMessages[0];
    }

    let forcedErrorMsg = eventError?.body?.errorMessage || null;

    let _type = String(responseMessages?.type || '')
      .trim()
      .toLowerCase();

    // si se envia customError como type se evita el interceptor para usar
    // uno custom en el componente ejemplo modalMassivePublishError
    if (_type === 'customerror') return null;
    // En aprobar/rechazar sugerencias, si backend responde success no mostrar warnings/information.
    if (responseSuccess && isAssortmentSuggestionDecisionRequest) return null;
    if (_type !== 'error' && _type !== 'warning' && _type !== 'information' && forcedErrorMsg == null) {
      return null;
    }

    let _errorMsg = null;
    let errorKey = 'CUSTOM_ERRORS.' + responseMessages?.keyword;
    let errorKeyAlt = 'INTERFACE_LOG_MSG_KEYS.' + responseMessages?.keyword;
    let errorValues = responseMessages?.values || null;

    //Si es un error code, intenta traducir
    if (responseMessages?.keyword) {
      errorValues = typeof errorValues === 'string' ? errorValues.split(',').map(ev => (ev ? ev.trim() : null)) : errorValues;
      _errorMsg = this.translate.instant(errorKey, errorValues) || this.translate.instant(errorKeyAlt, errorValues) || errorKey;
    }

    //Es solo un warning
    let _isWarning = _type === 'warning' || _type === 'information';

    if (_isWarning && _errorMsg) {
      _warn('\n\n[TokenInterceptorService] ONLY WARNING');
      this.globalAlertService.simpleWarning(_errorMsg, null, null, _type === 'warning');
      return;
    }

    //Si no puede traducir intenta con errorMessage
    if (_errorMsg == null) {
      _errorMsg = forcedErrorMsg && typeof forcedErrorMsg === 'string' && forcedErrorMsg.length ? forcedErrorMsg : null;
    }

    //Si no puede le asigna un msg genérico
    if (!_errorMsg?.length) {
      _warn(
        '\n\n[TokenInterceptorService] no errorMsg Text:',
        _errorMsg,
        { errorKey, errorValues, responseSuccess, responseMessages },
        '\n\n'
      );

      _errorMsg = this.translate.instant('COMP.ERROR_MESSAGE.SUBTITLE') || null;
    }

    //Mensaje Custom ok, lo envía como 406
    let statusId = 406;
    const error = new HttpErrorResponse({
      error: { ...eventError.body, status: statusId, forceMsg: _errorMsg, errorLink: responseMessages?.link || null, type: _type },
      status: statusId,
      statusText: _errorMsg,
    } as any);

    if (true) {
      console.warn(
        '\n\n[TokenInterceptorService] checkCustomError',
        {
          errorCode: eventError.body.errorCode,
          eventError,
          responseSuccess,
          responseMessages,
          errorMsg: _errorMsg,
          errorKey,
          errorValues,
          type: _type,
        },
        '\n\n'
      );
    }

    return error;
  }

  /**
   * Spaces → Range (CAT-1687): el backend puede responder 503 con `responseMessages[0].keyword`
   * sin `forceMsg`. El modal de error usa `forceMsg`; lo resolvemos solo por keyword (no por status).
   */
  private pickRangeServiceUnavailableMessage(err: HttpErrorResponse): string | null {
    const body = err?.error;
    const list = body?.responseMessages;
    if (!Array.isArray(list) || !list.length) return null;
    const kw = list[0]?.keyword;
    if (kw !== 'RANGE_SERVICE_UNAVAILABLE') return null;
    const primary = this.translate.instant('CUSTOM_ERRORS.RANGE_SERVICE_UNAVAILABLE');
    if (primary && !String(primary).startsWith('CUSTOM_ERRORS.')) return primary;
    const alt = this.translate.instant('INTERFACE_LOG_MSG_KEYS.RANGE_SERVICE_UNAVAILABLE');
    if (alt && !String(alt).startsWith('INTERFACE_LOG_MSG_KEYS.')) return alt;
    return null;
  }

  intercept<T>(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<T>> {
    let requestToForward = req;

    if (_enableTempGuestFlow && req.url.includes('identity/temporary-access/exchange')) {
      // El exchange del código temporal debe ser público y sin headers de auth heredados
      return next.handle(req);
    }

    if (req.url.includes('apiDataBricksLocal')) {
      //bypass apiDataBricksLocal
      return next.handle(req);
    }

    if (req.url.includes('api.deepseek') || req.url.includes('api.anthropic') || req.url.includes('anthropic/')) {
      //bypass deepseek/anthropic
      return next.handle(req);
    }

    if (this.oidcSecurityService === undefined) {
      this.oidcSecurityService = this.injector.get(OidcSecurityService);
    }

    if (this.oidcSecurityService !== undefined) {
      const tmpBrowserId: string = this.messagingService.getToken();

      if (!req.url.includes('assets/') /* No inyecta para locales */) {
        // CATMAN-B015 — Aislamiento de sesión temporal.
        // Si esta pestaña fue marcada como temporal (sessionStorage = per-tab),
        // NUNCA usar credenciales normales: ni el token in-memory de OIDC ni el
        // _v3___token persistido en localStorage compartido. Aunque OIDC haya
        // rehidratado al bootstrapear, la pestaña temporal queda aislada.
        if (_enableTempGuestFlow && this.tempAccessSession.isTabTemporary()) {
          const tempToken = this.tempAccessSession.getToken();
          if (tempToken && this.tempAccessSession.isAllowedGuestRequest(req.url)) {
            const tokenValue = 'Bearer ' + tempToken;
            const header = { Authorization: tokenValue };
            if (tmpBrowserId) (header as any).browserId = tmpBrowserId;
            // Mismos feature flags que la sesión normal: endpoints temporales que dependen
            // de FF (ej. FeatureFlag__CAT-1592 en el reporte) los leen del header server-side.
            (header as any).featureFlags = this.devSettingsService?.__getAllEnabledFeatureFlagsValues?.()?.join?.(',');
            requestToForward = req.clone({ setHeaders: header });
          }
          // Endpoint fuera del whitelist temporal o temp ya expirado:
          // sin header. Nunca fallback cruzado a sesión normal.
        } else {
          const token = this.oidcSecurityService.getToken();
          if (token !== '') {
            const tokenValue = 'Bearer ' + token;
            const header = { Authorization: tokenValue };

            if (tmpBrowserId) (header as any).browserId = tmpBrowserId;

            if (true) (header as any).featureFlags = this.devSettingsService?.__getAllEnabledFeatureFlagsValues?.()?.join?.(','); //2076

            if (true) (header as any)['Client-Date'] = new Date().toISOString();
            if (true) (header as any)['Client-Date-Array'] = _getFormatDate(Date.now(), true);

            requestToForward = req.clone({ setHeaders: header });

            if (_saveToken && !_tokenIsSaved) {
              // Save Token
              localStorage.setItem(_keyVer + '__token', tokenValue);
              _tokenIsSaved = true;
            }
          } else {
            if (_enableTempGuestFlow) {
              const tempToken = this.tempAccessSession.getToken();
              if (tempToken && this.tempAccessSession.isAllowedGuestRequest(req.url)) {
                const tokenValue = 'Bearer ' + tempToken;
                const header = { Authorization: tokenValue };
                if (tmpBrowserId) (header as any).browserId = tmpBrowserId;
                requestToForward = req.clone({ setHeaders: header });
              } else if (tempToken) {
                // Si existe token temporal, no usar token persistido ni OIDC fallback
                // fuera de los endpoints permitidos para invitados.
              } else {
                // id:8EqrPjg3oD
                const _token = localStorage.getItem(_keyVer + '__token');
                if (_saveToken && _token) {
                  // Load Token
                  const header = { Authorization: _token };
                  if (tmpBrowserId) (header as any).browserId = tmpBrowserId;
                  requestToForward = req.clone({ setHeaders: header });

                  if (_devTest) {
                    _log('[USE SAVED TOKEN]', req.url);
                  }
                } else {
                  if (_devTest) {
                    console.log('[NO TOKEN]', req.url);
                  }
                }
              }
            } else {
              // id:8EqrPjg3oD
              const _token = localStorage.getItem(_keyVer + '__token');
              if (_saveToken && _token) {
                // Load Token
                const header = { Authorization: _token };
                if (tmpBrowserId) (header as any).browserId = tmpBrowserId;
                requestToForward = req.clone({ setHeaders: header });

                if (_devTest) {
                  _log('[USE SAVED TOKEN]', req.url);
                }
              } else {
                if (_devTest) {
                  console.log('[NO TOKEN]', req.url);
                }
              }
            }
          }
        } // fin del else de isTabTemporary (CATMAN-B015)
      }
    } else {
      console.log('OidcSecurityService undefined: NO auth header!');
    }

    // 🔀 Bifurcación basada en Feature Flag CORE-100 (verificación en runtime)
    // Feature flags son asincrónicos, se cargan después del boot
    const useNewInterceptor = this.devSettingsService.hasFeatureFlag(FeatureFlagsKeysEnum['FeatureFlag__CORE-100']);

    if (true) _log(`🔀 [TokenInterceptorService] Usando ${useNewInterceptor ? 'NUEVO' : 'LEGACY'} interceptor`);

    if (useNewInterceptor !== true) {
      // 🔙 Versión LEGACY sin auto-retry (comportamiento original)
      return next.handle(requestToForward).pipe(
        map(event => {
          if (!environment || !event) return event;

          const error = this.checkCustomError(event);
          if (error != null) throw error;

          /*
          Para dev-azd hace un replace de las props de get-authconfig
          redirect_url
          silent_renew_url
          post_logout_redirect_uri

          remplaza forceOidcSecurityServiceRedirect (url) por forceOidcSecurityServiceRedirectBy
        */
          const acutalServiceRedirect = environment.forceOidcSecurityServiceRedirect;
          if (!acutalServiceRedirect) return event;

          const newServiceRedirect = environment.forceOidcSecurityServiceRedirectBy;
          if (!newServiceRedirect) return event;

          if (event instanceof HttpResponse) {
            const redirect_url = _get(event, 'body.redirect_url');
            const silent_renew_url = _get(event, 'body.silent_renew_url');
            const post_logout_redirect_uri = _get(event, 'body.post_logout_redirect_uri');

            if (redirect_url) event.body.redirect_url = redirect_url.replace(acutalServiceRedirect, newServiceRedirect);
            if (silent_renew_url) event.body.silent_renew_url = silent_renew_url.replace(acutalServiceRedirect, newServiceRedirect);
            if (post_logout_redirect_uri)
              event.body.post_logout_redirect_uri = post_logout_redirect_uri.replace(acutalServiceRedirect, newServiceRedirect);
          }
          return event;
        }),

        tap(
          (event: HttpEvent<any>) => {
            if (event instanceof HttpResponse) {
              // Load user permissions
              if (event.body && event.body.permission) {
                // TODO: check for an standard api response
                const perm = event.body.permission;
                this.permissionsService.loadPermissions(perm);
              }
            }
          },
          (err: any) => {
            if (err instanceof HttpErrorResponse) {
              if (
                this.router.url.includes('Logout') ||
                this.router.url.includes('user-missing-permission') ||
                (err?.url || '').includes('notification-log') /*No echar si es por notificaciones*/
              ) {
                return;
              }

              // fix: prevent interception of errors from Genie proxy
              if ((err?.url || '').includes('genieproxy/proxy')) return;

              if (err.status === 401) {
                //PLATFORM-SEC02 (CA-06): al usuario lo bloquearon con la sesión ya abierta. Se cierra la
                //sesión contra IdentityServer en vez de re-autorizar: reintentar es justamente lo que
                //produce el bucle infinito. El logoff limpia la cookie y lo deja en el login, donde al
                //intentar entrar ve el mensaje de usuario bloqueado.
                if (_isBlockedUserError(err)) {
                  this.httpCancelService.cancelPendingRequests();

                  if (this.oidcSecurityService === undefined) {
                    this.oidcSecurityService = this.injector.get(OidcSecurityService);
                  }

                  this.oidcSecurityService?.logoff();

                  return;
                }

                if (_enableTempGuestFlow && this.tempAccessSession.isValid()) {
                  this.tempAccessSession.clearSession();
                  this.router.navigate(['/unauthorized']);
                  return;
                }

                this.httpCancelService.cancelPendingRequests();

                if (this.oidcSecurityService === undefined) {
                  this.oidcSecurityService = this.injector.get(OidcSecurityService);
                }

                if (this.oidcSecurityService !== undefined) {
                  this.oidcSecurityService.authorize();
                }

                return;
              }

              if (err.status === 403) {
                if (_enableTempGuestFlow && this.tempAccessSession.isValid()) return;

                this.httpCancelService.cancelPendingRequests();
                setTimeout(() => {
                  let dataMsg = {};
                  if (true) {
                    (dataMsg as any).forceMsg = this.translate.instant('CUSTOM_ERRORS.UNAUTHORIZED_ACCESS');
                    (dataMsg as any).error = null;
                    (dataMsg as any).responseMessages = null;
                    if (true) console.log(['ERRORS_CUSTOM_BE_ERRORS'], { dataMsg: dataMsg });
                  }

                  if (true) console.warn('[dataMsg]', { dataMsg, err });

                  this.errorMessageService.showNoQueue(dataMsg);
                }, 256);

                return;
              }

              // intercept Generic Errors
              // https://developer.mozilla.org/es/docs/Web/HTTP/Status
              if (
                (err.status >= 500 && err.status <= 515) ||
                err.status === 404 || // Not Found
                err.status === 405 || // Method Not Allowed
                err.status === 406 || // Not Acceptable (custom errors)
                err.status === 413 || // Payload Too Large
                err.status === 414 || // URI Too Long
                err.status === 423 || // Locked (WebDAV)
                err.status === 429 || // Too Many Requests
                err.status === 432 || // Unassigned
                err.status === 451 // Unavailable For Legal Reasons
              ) {
                console.error('%c[error intercept]', 'color:#e93f3b', err);

                if (true && err.status === 413) {
                  setTimeout(() => {
                    this.errorMessageService.showNoQueue(
                      { forceMsg: this.translate.instant('CUSTOM_ERRORS.FileIsTooBig'), error: err },
                      err
                    );
                  }, 256);
                  return;
                }

                // Wait 2 dev error & show error modal
                setTimeout(() => {
                  if (true && this.statusConecctionService.delegateError() === true) return;

                  let dataMsg = {} as { forceMsg?: string; forceShow?: boolean; error?: any; responseMessages?: any };
                  if (err?.error?.forceMsg) {
                    dataMsg.forceMsg = err.error.forceMsg;
                    dataMsg.error = err.error;
                    dataMsg.responseMessages = err.error.responseMessages;
                    // Custom BE error con mensaje explícito de negocio → siempre visible aunque el tenant
                    // tenga DEMO-DISABLE-ERROR-MODALS (ese flag oculta errores técnicos, no de negocio).
                    dataMsg.forceShow = true;
                    if (true) console.log(['ERRORS_CUSTOM_BE_ERRORS'], { dataMsg: dataMsg });
                  } else {
                    const rangeUnavailable = this.pickRangeServiceUnavailableMessage(err);
                    if (rangeUnavailable) {
                      dataMsg.forceMsg = rangeUnavailable;
                      dataMsg.error = err.error;
                      dataMsg.responseMessages = err.error?.responseMessages;
                      dataMsg.forceShow = true;
                      if (true) console.log(['ERRORS_CUSTOM_BE_ERRORS'], { dataMsg: dataMsg, keyword: 'RANGE_SERVICE_UNAVAILABLE' });
                    }
                  }

                  if (true) console.warn('[dataMsg]', { dataMsg, forceMsg: err?.error?.forceMsg, err });

                  this.errorMessageService.showNoQueue(dataMsg, err);
                }, 256);

                return;
              }

              //Sin conexion
              if (err.status === 0 && true) {
                this.statusConecctionService.delegateError();
              }
            }
          }
        )
      );
    } else {
      // ✅ Versión CON auto-retry y modal

      const handleRequest$ = next.handle(requestToForward);
      const withRetry$ = this.applyAutoRetryIfEnabled(handleRequest$, requestToForward);

      return withRetry$.pipe(
        map(event => {
          if (!environment || !event) return event;

          const error = this.checkCustomError(event);
          if (error != null) throw error;

          /*
          Para dev-azd hace un replace de las props de get-authconfig
          redirect_url
          silent_renew_url
          post_logout_redirect_uri

          remplaza forceOidcSecurityServiceRedirect (url) por forceOidcSecurityServiceRedirectBy
        */
          const acutalServiceRedirect = environment.forceOidcSecurityServiceRedirect;
          if (!acutalServiceRedirect) return event;

          const newServiceRedirect = environment.forceOidcSecurityServiceRedirectBy;
          if (!newServiceRedirect) return event;

          if (event instanceof HttpResponse) {
            const redirect_url = _get(event, 'body.redirect_url');
            const silent_renew_url = _get(event, 'body.silent_renew_url');
            const post_logout_redirect_uri = _get(event, 'body.post_logout_redirect_uri');

            if (redirect_url) event.body.redirect_url = redirect_url.replace(acutalServiceRedirect, newServiceRedirect);
            if (silent_renew_url) event.body.silent_renew_url = silent_renew_url.replace(acutalServiceRedirect, newServiceRedirect);
            if (post_logout_redirect_uri)
              event.body.post_logout_redirect_uri = post_logout_redirect_uri.replace(acutalServiceRedirect, newServiceRedirect);
          }
          return event;
        }),

        tap(
          (event: HttpEvent<any>) => {
            if (event instanceof HttpResponse) {
              // Load user permissions
              if (event.body && event.body.permission) {
                // TODO: check for an standard api response
                const perm = event.body.permission;
                this.permissionsService.loadPermissions(perm);
              }
            }
          },
          (err: any) => {
            // Si el error está siendo procesado por retry, no hacer nada aquí
            // Solo se procesa cuando: no pasó por retry O ya se agotaron los reintentos
            if ((err as any).__retrying === true) return;

            if (_isFeDev()) _log('<<[HTTP ERROR]>>', err.status);

            if (err instanceof HttpErrorResponse) {
              if (
                this.router.url.includes('Logout') ||
                this.router.url.includes('user-missing-permission') ||
                (err?.url || '').includes('notification-log') /*No echar si es por notificaciones*/
              ) {
                return;
              }

              // fix: prevent interception of errors from Genie proxy
              if ((err?.url || '').includes('genieproxy/proxy')) return;
              if ((err?.url || '').includes('api/datascience')) return;

              if (err.status === 401) {
                //PLATFORM-SEC02 (CA-06): al usuario lo bloquearon con la sesión ya abierta. Se cierra la
                //sesión contra IdentityServer en vez de re-autorizar: reintentar es justamente lo que
                //produce el bucle infinito. El logoff limpia la cookie y lo deja en el login, donde al
                //intentar entrar ve el mensaje de usuario bloqueado.
                if (_isBlockedUserError(err)) {
                  this.httpCancelService.cancelPendingRequests();

                  if (this.oidcSecurityService === undefined) {
                    this.oidcSecurityService = this.injector.get(OidcSecurityService);
                  }

                  this.oidcSecurityService?.logoff();

                  return;
                }

                if (_enableTempGuestFlow && this.tempAccessSession.isValid()) {
                  this.tempAccessSession.clearSession();
                  this.router.navigate(['/unauthorized']);
                  return;
                }

                this.httpCancelService.cancelPendingRequests();

                if (this.oidcSecurityService === undefined) {
                  this.oidcSecurityService = this.injector.get(OidcSecurityService);
                }

                if (this.oidcSecurityService !== undefined) {
                  this.oidcSecurityService.authorize();
                }

                return;
              }

              if (err.status === 403) {
                if (_enableTempGuestFlow && this.tempAccessSession.isValid()) return;

                this.httpCancelService.cancelPendingRequests();

                setTimeout(() => {
                  let dataMsg = {};

                  if (true) {
                    (dataMsg as any).forceMsg = this.translate.instant('CUSTOM_ERRORS.UNAUTHORIZED_ACCESS');
                    (dataMsg as any).error = null;
                    (dataMsg as any).responseMessages = null;
                    if (true) console.log(['ERRORS_CUSTOM_BE_ERRORS'], { dataMsg: dataMsg });
                  }

                  if (true) console.warn('[dataMsg]', { dataMsg, err });

                  this.errorMessageService.showNoQueue(dataMsg);
                }, 256);

                return;
              }

              // intercept Generic Errors
              // https://developer.mozilla.org/es/docs/Web/HTTP/Status
              if (
                (err.status >= 500 && err.status <= 515) ||
                err.status === 404 || // Not Found
                err.status === 405 || // Method Not Allowed
                err.status === 406 || // Not Acceptable (custom errors)
                err.status === 413 || // Payload Too Large
                err.status === 414 || // URI Too Long
                err.status === 423 || // Locked (WebDAV)
                err.status === 429 || // Too Many Requests
                err.status === 432 || // Unassigned
                err.status === 451 // Unavailable For Legal Reasons
              ) {
                console.error('%c[error intercept]', 'color:#e93f3b', err);

                if (true && err.status === 413) {
                  setTimeout(() => {
                    this.errorMessageService.showNoQueue(
                      { forceMsg: this.translate.instant('CUSTOM_ERRORS.FileIsTooBig'), error: err },
                      err
                    );
                  }, 256);
                  return;
                }

                // Wait 2 dev error & show error modal
                setTimeout(() => {
                  if (true && this.statusConecctionService.delegateError() === true) return;

                  let dataMsg = {} as { forceMsg?: string; forceShow?: boolean; error?: any; responseMessages?: any };

                  if (err?.error?.forceMsg) {
                    dataMsg.forceMsg = err.error.forceMsg;
                    dataMsg.error = err.error;
                    dataMsg.responseMessages = err.error.responseMessages;
                    // Custom BE error con mensaje explícito de negocio → siempre visible aunque el tenant
                    // tenga DEMO-DISABLE-ERROR-MODALS (ese flag oculta errores técnicos, no de negocio).
                    dataMsg.forceShow = true;
                    if (true) console.log(['ERRORS_CUSTOM_BE_ERRORS'], { dataMsg: dataMsg });
                  } else {
                    const rangeUnavailable = this.pickRangeServiceUnavailableMessage(err);
                    if (rangeUnavailable) {
                      dataMsg.forceMsg = rangeUnavailable;
                      dataMsg.error = err.error;
                      dataMsg.responseMessages = err.error?.responseMessages;
                      dataMsg.forceShow = true;
                      if (true) console.log(['ERRORS_CUSTOM_BE_ERRORS'], { dataMsg: dataMsg, keyword: 'RANGE_SERVICE_UNAVAILABLE' });
                    }
                  }

                  if (true) console.warn('[dataMsg]', { dataMsg, forceMsg: err?.error?.forceMsg, err });

                  this.errorMessageService.showNoQueue(dataMsg, err);
                }, 256);

                return;
              }

              //Sin conexion
              if (err.status === 0 && true) {
                _warn('🔍 [DEBUG] Intentando llamar delegateError() para status 0');
                const delegateResult = this.statusConecctionService.delegateError();
                _warn('🔍 [DEBUG] delegateError() retornó:', delegateResult);
              } else {
                _warn('🔍 [DEBUG] NO se llamó delegateError(), status:', err.status);
              }
            }
          }
        )
      );
    }
  }

  /**
   * 🔄 Aplica reintentos automáticos en caso de errores de conexión TCP (status 0)
   * Solo se activa si autoRetryOnTcpAbort está habilitado en la configuración
   * Y si el Feature Flag CORE-100 está activado
   *
   * @param request$ - Observable de la petición HTTP
   * @param req - HttpRequest original para verificar método HTTP
   * @returns Observable con lógica de retry aplicada si está habilitada
   */
  private applyAutoRetryIfEnabled<T>(request$: Observable<HttpEvent<T>>, req?: HttpRequest<any>): Observable<HttpEvent<T>> {
    const config = environment?.statusConnectionConfig;
    const autoRetryEnabled = config?.autoRetryOnTcpAbort === true;
    const maxAttempts = config?.autoRetryMaxAttempts || 3;
    const autoRetryOn504 = config?.autoRetryOn504TimeOut === true;

    // Feature deshabilitada, retornar request sin modificar
    if (!autoRetryEnabled) return request$;

    // URLs que NO deben reintentarse para evitar carga innecesaria
    const shouldNotRetry =
      req?.url?.includes('notification-log') ||
      req?.url?.includes('Logout') ||
      req?.url?.includes('genieproxy/proxy') ||
      req?.url?.includes('api/datascience');

    if (shouldNotRetry) return request$;

    let attemptCount = 0;

    // Aplicar retry con lógica condicional
    return request$.pipe(
      retryWhen(errors =>
        errors.pipe(
          tap(error => {
            if (!(error instanceof HttpErrorResponse)) throw error;

            // Verificar si es seguro reintentar (solo GET/HEAD para evitar duplicación)
            const isSafeMethod = !req || req.method === 'GET' || req.method === 'HEAD';

            // Errores recuperables muy conservadores:
            // - 0: Corte TCP / Sin conexión
            // - 502: Bad Gateway (proxy/gateway temporalmente no disponible)
            // - 503: Service Unavailable (servicio temporalmente sobrecargado)
            // - 504: Gateway Timeout (timeout en el gateway/proxy) - controlado por config
            const isRecoverableError =
              error.status === 0 || // Corte TCP
              (isSafeMethod && error.status === 502) || // Bad Gateway (solo GET/HEAD)
              (isSafeMethod && error.status === 503) || // Service Unavailable (solo GET/HEAD)
              (isSafeMethod && autoRetryOn504 && error.status === 504); // Gateway Timeout (solo GET/HEAD, y si está habilitado en config)

            if (!isRecoverableError) {
              // Error no recuperable (404, 500, etc.) - no reintentar
              throw error;
            }

            attemptCount++;

            if (attemptCount < maxAttempts) {
              // Marcar que este error está siendo procesado por retry
              (error as any).__retrying = true;

              // Continuar con el reintento
              const errorName = {
                0: 'Corte TCP',
                502: 'Bad Gateway',
                503: 'Service Unavailable',
                ...(autoRetryOn504 && { 504: 'Gateway Timeout' }),
              }[error.status];

              _warn(`🔄 [AUTO-RETRY ${attemptCount}/${maxAttempts}] Reintentando petición por ${errorName} (${error.url})`);
            } else {
              // Se agotaron los reintentos
              _warn(`🔄 [AUTO-RETRY] Se agotaron los ${maxAttempts} reintentos para: ${error.url} (status: ${error.status})`);

              // CRÍTICO: Borrar el flag para que el handler lo procese
              // (el mismo objeto error se reutiliza en cada reintento)
              delete (error as any).__retrying;

              throw error;
            }
          }),
          delay(500), // Delay reducido: 500ms entre reintentos
          take(maxAttempts) // Limitar a N reintentos
        )
      )
    );
  }

  /**
   * 🧪 Chaos Middleware - Test de condiciones de red adversas
   *
   * Llamar desde la consola del navegador:
   *
   * Ejemplos:
   * - window.__testChaos('abort')         // Simular corte TCP
   * - window.__testChaos('timeout')       // Simular timeout (10s)
   * - window.__testChaos('500')           // Simular error 500
   * - window.__testChaos('random')        // Modo aleatorio
   * - window.__testChaos('abort', 3000)   // Con latencia de 3 segundos
   *
   * @param mode - Tipo de simulación: 'abort' | 'timeout' | '500' | 'random'
   * @param latencyMs - Latencia opcional en milisegundos
   * @param url - URL opcional para probar (por defecto usa una rápida del sistema)
   *
   * https://docs.google.com/document/d/1XwjU_AMSU5fcqgEIDAQerbINdCwJF_NxKHKJEENU7e4/edit?tab=t.0
   */
  public testChaosMode(mode: string = 'abort', latencyMs?: number, url?: string): void {
    const testUrl = url || _get(environment, 'apiBaseUrl_prices') + 'categories/list-tree';
    // const testUrl = 'https://qa.prismaretail.dev/api/categories/list-tree';

    const headers: any = {
      'Content-Type': 'application/json',
      'X-Chaos-Simulate': mode,
    };

    if (latencyMs) {
      headers['X-Chaos-Latency-Ms'] = String(latencyMs);
    }

    const cudOptions = {
      headers: new HttpHeaders(headers),
    };

    console.warn('🧪 [CHAOS TEST] Iniciando prueba...', {
      mode,
      latency: latencyMs ? `${latencyMs}ms` : 'default',
      url: testUrl,
      headers,
    });

    console.time('🧪 CHAOS TEST');

    this.http
      .get(testUrl, cudOptions)
      .pipe(
        tap(data => {
          console.timeEnd('🧪 CHAOS TEST');
          console.log('✅ [CHAOS TEST] Respuesta exitosa:', data);
        }),
        catchError(error => {
          console.timeEnd('🧪 CHAOS TEST');

          // Status 0 = error de red (abort, timeout, no conexión)
          // Status 500 = error del servidor
          const errorType = error.status === 0 ? 'Error de Red/Conexión' : 'Error del Servidor';

          console.error(`❌ [CHAOS TEST] ${errorType}:`, {
            status: error.status,
            statusText: error.statusText,
            message: error.message,
            url: error.url,
            nota: error.status === 0 ? '✓ Comportamiento esperado para abort/timeout' : '',
          });

          throw error;
        })
      )
      .subscribe();
  }
}
